<template>
	<div class="form__field multiple-file-field" :class="`--${size}`">
		<div class="multiple-file-input-container" @focusout="handleFocusOut" @focusin="handleFocusIn">
			<label :for="name" class="label">
				<span>{{ label }}</span>
				<span v-if="required" class="required-symbol">*</span>
			</label>
			<input ref="input" class="multiple-file-input" type="file" :id="name" :name="name" @change="input"
				:multiple="multiple > 1" :accept="accept" :disabled="modelValue.file?.length >= multiple" />
			<label class="fake-multiple-file-input" :for="name">
				<span class="fake-multiple-button btn --white --border">{{ $t(`form.label.select_file_${multiple > 1 ? 'multiple' : 'single' }`) }}</span>
			</label>
			<p v-if="message" class="note">{{ message }}</p>
			<p :class="['input-error', { '--hidden': !showError }]">
				{{ $t(`form.errors.${name}`) }}
			</p>
		</div>
		<div class="multiple-file-preview">
			<div v-for="image, key in modelValue.file" :key="`image-preview-${key}`" class="multiple-file-preview__file-container bg-off">
				<img :src="getImageUrl(image)" alt="Aperçu de l'image" class="multiple-file-preview__image" />
				<button class="close-button btn --white --border --icon" type="button" @click="removeFile(image)">
					<Icon id="close" />
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { Icon } from '@/components/global'

export default {
	name: 'FormMultipleFile',
	props: {
		label: {
			type: String,
			required: true
		},
		modelValue: {
			type: Object,
			required: true
		},
		size: {
			type: String,
			default: null
		},
		message: {
			type: String,
			default: null
		},
		name: {
			type: String,
			required: true
		},
		required: {
			type: Boolean,
			default: false
		},
		multiple: {
			type: Number,
			default: 1
		},
		accept: {
			type: String,
			default: null
		},
		preview: {
			type: Boolean,
			default: false
		},
		maxSize: {
			type: Number,
			default: 2048
		}
	},
	components: {
		Icon
	},
	data() {
		return {
			hasFocusedIn: false,
			hasFocusedOut: false,
			filePreview: null,
			showError: false
		}
	},
	methods: {
		handleFocusIn(e) {
			this.hasFocusedIn = true
			this.input(e)

			setTimeout(() => {
				const files = [...this.modelValue.file]

				const isValid = this.validateFiles(files)

				if (this.hasFocusedOut && !isValid) {
					this.showError = true
				}
				else if (isValid) {
					this.showError = false
				}
			}, 500)
		},
		async handleFocusOut(e) {
			this.input(e)

			setTimeout(() => {
				const files = [...this.modelValue.file]

				this.hasFocusedOut = true
				const isValid = this.validateFiles(files)

				if (!document.hasFocus() || isValid) {
					this.showError = false
				}

				if (document.hasFocus() && !isValid) {
					this.showError = true
				}
			}, 500)
		},
		input(event) {
			const newFiles = Array.from(event.target.files || [])

			const existingItems = Array.isArray(this.modelValue.file)
				? [...this.modelValue.file]
				: []

			const merged = [...existingItems]

			newFiles.forEach(newFile => {
				const isDuplicate = merged.some(existing => {
					if (existing instanceof File) {
						return (
							existing.name === newFile.name &&
							existing.size === newFile.size &&
							existing.lastModified === newFile.lastModified
						)
					}

					return existing.name === newFile.name
				})

				if (!isDuplicate) {
					merged.push(newFile)
				}
			})

			const isValid = this.validateFiles(merged)

			this.$emit('update:modelValue', {
				...this.modelValue,
				file: merged,
				valid: isValid
			})

			event.target.value = ''
		},
		validateFiles(files) {
			if (!Array.isArray(files)) return false

			if (this.required && files.length === 0) return false

			if (files.length > this.multiple) return false

			const totalSizeKb = files.reduce((total, file) => {
				if (file instanceof File) return total + file.size / 1024
				return total
			}, 0)

			if (totalSizeKb > this.maxSize) return false

			return true
		},
		getImageUrl(image) {
			if (image instanceof File) {
				return URL.createObjectURL(image)
			} else {
				return image?.url
			}
		},
		removeFile(image) {
			let files = [...this.modelValue.file]
			const index = files.indexOf(image)
			if (index !== -1)  files.splice(index, 1)

			const isValid = this.validateFiles(files)

			this.$emit('update:modelValue', {
				...this.modelValue,
				file: files,
				valid: isValid
			})
		}
	},
	computed: {
		stillInWebPage() {
			return document.hasFocus()
		}
	},
	emits: ['update:modelValue'],
	mounted() {
		this.$emit('update:modelValue', {
			file: [],
			previewName: this.modelValue.previewName || null,
			previewUrl: this.modelValue.previewUrl || null,
			value: '',
			valid: this.validateFiles([])
		})
	}
}
</script>